import dot from 'dot-object';
import { useEffect, useRef, useState } from 'react';
import { cn } from '../../../common/functions/cn';
import useTestTag from '../../../common/hooks/use-test-tag';
import type { MakeRequired } from '../../../common/types/make-required';
import RequiredIndicator from '../../common/required-indicator/required-indicator.component';
import type { DynamicFieldComponentProps } from '../../dynamic-form/dynamic-form.interfaces';
import type { JsonData, JsonDataItem } from '../../generic-types';
import ErrorLabel from '../../labels/error-label';
import FieldLabel from '../../labels/field-label';
import type { CheckboxChangeEventType } from '../checkbox/checkbox';
import { Checkbox } from '../checkbox/checkbox';
import SelectButton from '../select-button/select-button.component';

export interface CheckboxGroupResult {
  value: unknown;
  text: string;
  color?: string;
}

export interface CheckBoxGroupProps
  extends MakeRequired<DynamicFieldComponentProps<Array<CheckboxGroupResult>>, 'name'> {
  data?: JsonData;
  textField: string;
  valueField: string;
  className?: string;
  multiSelectCheckboxType?: 'buttons' | 'checkboxes';
  checkboxGroupStyling?: string;
  archivedField?: string;
  showSelectedArchivedOptions?: boolean; // Feature flag to show selected archived options works in combination with archivedField
}

const CheckboxGroup = ({
  name,
  label,
  error,
  value,
  inputRef,
  data,
  textField,
  valueField,
  readOnly,
  required,
  onChange,
  disabled,
  className,
  multiSelectCheckboxType = 'checkboxes',
  labelStyles,
  checkboxGroupStyling,
  archivedField,
  showSelectedArchivedOptions = false,
}: CheckBoxGroupProps) => {
  const [selectedValues, setSelectedValues] = useState<Array<CheckboxGroupResult>>([]);
  const initialSelectedValues = useRef<Array<CheckboxGroupResult>>([]);

  const isCheckBox = multiSelectCheckboxType === 'checkboxes';

  useEffect(() => {
    const newValues = Array.isArray(value) ? value : [];
    setSelectedValues(newValues);
    if (initialSelectedValues.current.length === 0 && newValues.length > 0) {
      initialSelectedValues.current = newValues;
    }
  }, [value]);

  const tagCreator = useTestTag(label || name || '');

  const isSelected = (item: JsonDataItem): boolean => {
    return Array.isArray(selectedValues) && selectedValues.some((_item) => _item.value == item[valueField]);
  };

  const wasInitiallySelected = (item: JsonDataItem): boolean => {
    return initialSelectedValues.current.some((_item) => _item.value == item[valueField]);
  };

  const shouldRenderOption = (item: JsonDataItem): boolean => {
    if (!showSelectedArchivedOptions || !archivedField) {
      return true;
    } else {
      const isArchived = !!dot.pick(archivedField, item);

      if (!isArchived) {
        return true;
      } else {
        return wasInitiallySelected(item);
      }
    }
  };

  const handleChange = (checkboxChangeEvent: CheckboxChangeEventType, dataItem: JsonDataItem) => {
    const { value, checked } = checkboxChangeEvent;

    let update: Array<CheckboxGroupResult>;
    if (checked) {
      update = [...selectedValues, { value: value, text: dataItem[textField] as string }];
    } else {
      update = selectedValues.filter((item) => item.value != value);
    }

    setSelectedValues(update);
    onChange?.(update);
  };

  const containerClasses = cn('flex flex-col gap-1', className);
  // SelectButton (the `buttons` variant) has no per-item invalid styling, so fall back to a
  // group-level border for the error state there. The `checkboxes` variant gets invalid styling
  // on each child Checkbox via the `invalid` prop instead.
  const elementClasses = cn('flex flex-col gap-2', checkboxGroupStyling, {
    'rounded-md border border-error': error && !readOnly && !isCheckBox,
  });

  const renderItem = (item: JsonDataItem, i: number) => {
    const isItemSelected = isSelected(item);

    return (
      <Checkbox
        key={`${name}_item_${i}`}
        name={name}
        label={item[textField] as string}
        value={item[valueField]}
        checked={isItemSelected}
        onChange={(value) => value && handleChange(value, item)}
        readOnly={readOnly}
        invalid={!!error}
        disabled={disabled}
      />
    );
  };

  const renderItems = (finalData?: JsonData) => {
    if (!finalData) return null;
    return finalData.filter(shouldRenderOption).map(renderItem);
  };

  const renderSelectButtons = (finalData?: JsonData) => {
    if (!finalData) return null;
    const filteredData = finalData.filter(shouldRenderOption);

    return (
      <SelectButton
        id={name}
        textField={textField}
        valueField={valueField}
        inputRef={inputRef}
        value={selectedValues || []}
        data={filteredData}
        onChange={onChange}
        readOnly={readOnly}
        error={error}
        disabled={disabled}
        showError={false}
      />
    );
  };

  return (
    <div className={containerClasses}>
      {label && name && (
        <div className="flex">
          <FieldLabel label={label} name={name} labelStyles={labelStyles} />
          <RequiredIndicator readOnly={readOnly} required={required} />
        </div>
      )}
      <div className={elementClasses} ref={tagCreator('checkbox-group')}>
        {isCheckBox ? renderItems(data) : renderSelectButtons(data)}
      </div>
      {error && <ErrorLabel>{error}</ErrorLabel>}
    </div>
  );
};

export default CheckboxGroup;
